import {
  CANVAS_ELEMENT_TYPES,
  TRANSPARENT_CANVAS_FILL,
} from "../nativeCanvasElements";
import {
  DEFAULT_CANVAS_FONT_FAMILY,
  DEFAULT_CANVAS_FONT_STYLE,
  DEFAULT_CANVAS_FONT_WEIGHT,
  getCanvasFontStack,
} from "../canvasFonts";
import { getArrowPoints, getElementFrame } from "../geometry/elementBounds";
import { getArrowHeadPoints } from "../geometry/arrowGeometry";
import { getShapeLabelLayout } from "../geometry/shapeTextLayout";
import {
  DEFAULT_CANVAS_PALETTE,
  resolveShapeFillColor,
  resolveShapeLabelColor,
  resolveStrokeColor,
  resolveTextColor,
} from "./canvasTheme";
import {
  getElementSeed,
  strokeMarkerPath,
  traceRoughArrowHead,
  traceRoughArrowShaft,
  traceRoughDiamond,
  traceRoughEllipse,
  traceRoughRectangle,
} from "./roughStyle";

const DEFAULT_FILL_COLOR = "#111827";
const DEFAULT_STROKE_COLOR = "#ff9f43";
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_TEXT_COLOR = "#f8fafc";
const DEFAULT_TEXT_SIZE = 20;
const IMAGE_PLACEHOLDER_FILL = "rgba(148, 163, 184, 0.2)";
const TEXT_LINE_HEIGHT_FACTOR = 1.3;

const SHAPE_TRACERS = Object.freeze({
  [CANVAS_ELEMENT_TYPES.DIAMOND]: traceRoughDiamond,
  [CANVAS_ELEMENT_TYPES.ELLIPSE]: traceRoughEllipse,
  [CANVAS_ELEMENT_TYPES.RECTANGLE]: traceRoughRectangle,
});

const estimateEllipsePerimeter = ({ height, width }) => {
  const radiusX = width / 2;
  const radiusY = height / 2;
  const squaredDifference = (radiusX - radiusY) ** 2;
  const squaredSum = (radiusX + radiusY) ** 2;
  const ratio = squaredSum ? squaredDifference / squaredSum : 0;
  return Math.PI * (radiusX + radiusY) * (1 + (3 * ratio) / (10 + Math.sqrt(4 - 3 * ratio)));
};

const estimateShapePathLength = ({ element, frame }) => {
  if (element.type === CANVAS_ELEMENT_TYPES.ELLIPSE) {
    return estimateEllipsePerimeter(frame);
  }
  if (element.type === CANVAS_ELEMENT_TYPES.DIAMOND) {
    return 4 * Math.hypot(frame.width / 2, frame.height / 2);
  }
  return 2 * (frame.width + frame.height);
};

const estimateArrowPathLength = (points) => {
  const { leftWing, rightWing, tip } = getArrowHeadPoints(points);
  const shaftLength = Math.hypot(points.x2 - points.x1, points.y2 - points.y1);
  const leftLength = Math.hypot(tip.x - leftWing.x, tip.y - leftWing.y);
  const rightLength = Math.hypot(tip.x - rightWing.x, tip.y - rightWing.y);
  return shaftLength + leftLength + rightLength;
};

const getStyleValue = ({ element, fallback, key }) =>
  element.style?.[key] ?? element[key] ?? fallback;

const getStrokeStyle = ({ element, palette }) => ({
  stroke: resolveStrokeColor({
    color: getStyleValue({ element, fallback: DEFAULT_STROKE_COLOR, key: "stroke" }),
    palette,
  }),
  strokeWidth: Number(
    getStyleValue({ element, fallback: DEFAULT_STROKE_WIDTH, key: "strokeWidth" })
  ),
});

const setTextStyle = ({ ctx, element }) => {
  const fontSize = getStyleValue({
    element,
    fallback: DEFAULT_TEXT_SIZE,
    key: "fontSize",
  });
  const fontStyle = getStyleValue({
    element,
    fallback: DEFAULT_CANVAS_FONT_STYLE,
    key: "fontStyle",
  });
  const fontWeight = getStyleValue({
    element,
    fallback: DEFAULT_CANVAS_FONT_WEIGHT,
    key: "fontWeight",
  });
  const familyId = getStyleValue({
    element,
    fallback: DEFAULT_CANVAS_FONT_FAMILY,
    key: "fontFamily",
  });
  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${getCanvasFontStack(familyId)}`;
  return fontSize;
};

const drawShapeLabel = ({ ctx, element, frame, palette, shapeFill }) => {
  if (!element.label) return;

  setTextStyle({ ctx, element });
  const layout = getShapeLabelLayout({
    element,
    measureText: (text) => ctx.measureText(text).width,
  });
  ctx.fillStyle = resolveShapeLabelColor({
    color: getStyleValue({ element, fallback: DEFAULT_TEXT_COLOR, key: "color" }),
    fill: shapeFill,
    palette,
  });
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const centerX = frame.x + frame.width / 2;
  const textHeight = layout.lines.length * layout.lineHeight;
  const firstLineY = frame.y + (frame.height - textHeight) / 2 + layout.lineHeight / 2;
  layout.lines.forEach((line, lineIndex) => {
    ctx.fillText(line, centerX, firstLineY + lineIndex * layout.lineHeight);
  });
};

const drawBoxShape = ({ ctx, element, isTextEditing, palette }) => {
  const frame = getElementFrame(element);
  const seed = getElementSeed(element.id);
  const traceShape = SHAPE_TRACERS[element.type];
  const fill = resolveShapeFillColor({
    color: getStyleValue({ element, fallback: DEFAULT_FILL_COLOR, key: "fill" }),
    palette,
  });
  const { stroke, strokeWidth } = getStrokeStyle({ element, palette });

  if (fill !== TRANSPARENT_CANVAS_FILL) {
    ctx.beginPath();
    traceShape({ ctx, frame, seed });
    ctx.fillStyle = fill;
    ctx.fill();
  }
  ctx.strokeStyle = stroke;
  ctx.lineJoin = "round";
  strokeMarkerPath({
    ctx,
    pathLength: estimateShapePathLength({ element, frame }),
    seed,
    strokeWidth,
    tracePath: (passOffset) => traceShape({ ctx, frame, passOffset, seed }),
  });
  if (!isTextEditing) {
    drawShapeLabel({ ctx, element, frame, palette, shapeFill: fill });
  }
};

const drawArrow = ({ ctx, element, palette }) => {
  const points = getArrowPoints(element);
  const seed = getElementSeed(element.id);
  const { stroke, strokeWidth } = getStrokeStyle({ element, palette });

  ctx.strokeStyle = stroke;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  strokeMarkerPath({
    ctx,
    pathLength: estimateArrowPathLength(points),
    seed,
    strokeWidth,
    tracePath: (passOffset) => {
      traceRoughArrowShaft({ ctx, passOffset, points, seed });
      traceRoughArrowHead({ ctx, passOffset, points, seed });
    },
  });
};

const drawFreehandPath = ({ ctx, element, palette }) => {
  const points = Array.isArray(element.points) ? element.points : [];
  if (!points.length) return;
  const { stroke, strokeWidth } = getStrokeStyle({ element, palette });

  ctx.strokeStyle = stroke;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
  ctx.stroke();
};

const drawText = ({ ctx, element, isTextEditing, palette }) => {
  if (isTextEditing) return;
  const fontSize = setTextStyle({ ctx, element });
  ctx.fillStyle = resolveTextColor({
    color: getStyleValue({ element, fallback: DEFAULT_TEXT_COLOR, key: "fill" }),
    palette,
  });
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const lines = String(element.text ?? element.content ?? "").split("\n");
  lines.forEach((line, lineIndex) => {
    ctx.fillText(
      line,
      element.x ?? 0,
      (element.y ?? 0) + lineIndex * fontSize * TEXT_LINE_HEIGHT_FACTOR
    );
  });
};

const drawImage = ({ ctx, element, getImage }) => {
  const frame = getElementFrame(element);
  const image = getImage?.(element.src);
  if (image) {
    ctx.drawImage(image, frame.x, frame.y, frame.width, frame.height);
    return;
  }
  ctx.fillStyle = IMAGE_PLACEHOLDER_FILL;
  ctx.fillRect(frame.x, frame.y, frame.width, frame.height);
};

const ELEMENT_RENDERERS = Object.freeze({
  [CANVAS_ELEMENT_TYPES.ARROW]: drawArrow,
  [CANVAS_ELEMENT_TYPES.DIAMOND]: drawBoxShape,
  [CANVAS_ELEMENT_TYPES.ELLIPSE]: drawBoxShape,
  [CANVAS_ELEMENT_TYPES.IMAGE]: drawImage,
  [CANVAS_ELEMENT_TYPES.PATH]: drawFreehandPath,
  [CANVAS_ELEMENT_TYPES.RECTANGLE]: drawBoxShape,
  [CANVAS_ELEMENT_TYPES.TEXT]: drawText,
  freehand: drawFreehandPath,
});

/**
 * Draws one element in scene coordinates onto an already-transformed context.
 * `isTextEditing` suppresses the element's text/label so the chromeless HTML
 * editor overlay is the only visible copy while editing. `palette` carries
 * the active theme's canvas colors; theme-authored default colors stored on
 * elements resolve through it.
 * @param {{ ctx: CanvasRenderingContext2D, element: object, getImage?: Function, isTextEditing?: boolean, palette?: { shapeFill: string, text: string } }} params
 * @returns {void}
 */
export const drawCanvasElement = ({
  ctx,
  element,
  getImage,
  isTextEditing = false,
  palette = DEFAULT_CANVAS_PALETTE,
}) => {
  const renderElement = ELEMENT_RENDERERS[element.type];
  if (renderElement) {
    renderElement({ ctx, element, getImage, isTextEditing, palette });
  }
};
